import React, { useState, useEffect, useRef } from 'react'
import { createWorker } from 'tesseract.js'
import Alert from './shared/Alert'

const ScanDocumentRoot = () => {
  const [capturedImages, setCapturedImages] = useState([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const videoRef = useRef(null)
  const workerRef = useRef(null)
  const [error, setError] = useState(null)
  const [showCamera, setShowCamera] = useState(true)

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent))
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Initialize camera and Tesseract worker only on mobile
  useEffect(() => {
    if (!isMobile) return

    const initialize = async () => {
      // Initialize Tesseract
      workerRef.current = await createWorker()

      if (showCamera) {
        // Initialize camera only if in camera mode
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'environment' },
          })
          if (videoRef.current) {
            videoRef.current.srcObject = stream
          }
        } catch (err) {
          console.error('Camera initialization failed:', err)
          setShowCamera(false) // Fall back to upload mode if camera fails
        }
      }
    }

    initialize()

    // Cleanup
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate()
      }
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach(track => track.stop())
      }
    }
  }, [isMobile, showCamera])

  const captureImage = async () => {
    if (!videoRef.current) return

    const canvas = document.createElement('canvas')
    canvas.width = videoRef.current.videoWidth
    canvas.height = videoRef.current.videoHeight
    const ctx = canvas.getContext('2d')

    ctx.drawImage(videoRef.current, 0, 0)
    const dataUri = canvas.toDataURL('image/jpeg', 0.8)

    setCapturedImages(prev => [
      ...prev,
      {
        dataUri: dataUri,
        type: 'image/jpeg',
        name: `captured_${Date.now()}.jpg`,
        source: 'camera',
      },
    ])
  }

  const handleFileUpload = event => {
    const files = Array.from(event.target.files)

    files.forEach(file => {
      const reader = new FileReader()
      reader.onloadend = () => {
        setCapturedImages(prev => [
          ...prev,
          {
            dataUri: reader.result,
            type: file.type,
            name: file.name,
            source: 'upload',
          },
        ])
      }
      reader.readAsDataURL(file)
    })
  }

  const removeImage = indexToRemove => {
    setCapturedImages(prev => prev.filter((_, index) => index !== indexToRemove))
  }

  const getCustomerDocumentId = () => {
    const pathParts = window.location.pathname.split('/')
    const soloSettlesIndex = pathParts.findIndex(part => part === 'solosettles')
    const customerDocumentId =
      soloSettlesIndex >= 0 && pathParts.length > soloSettlesIndex + 1
        ? pathParts[soloSettlesIndex + 1]
        : null

    if (!customerDocumentId) {
      throw new Error('Could not find customer document ID')
    }

    console.log('customerDocumentId', customerDocumentId)
    return customerDocumentId
  }

  const handleDone = async () => {
    setIsProcessing(true)

    try {
      const customerDocumentId = getCustomerDocumentId()

      // Determine which method was used
      const hasCameraImages = capturedImages.some(
        item => typeof item === 'object' && item.source === 'camera'
      )
      const hasUploadImages = capturedImages.some(
        item => typeof item === 'object' && item.source === 'upload'
      )

      // Upload all files in parallel
      const uploadPromises = capturedImages.map((item, index) => {
        const dataUri = item.dataUri
        const type = item.type
        const isPdf = type.includes('pdf')
        const blob = dataURItoBlob(dataUri)
        const extension = isPdf ? 'pdf' : 'jpg'
        const file = new File([blob], `file_${index + 1}.${extension}`, { type })

        const formData = new FormData()
        formData.append('file', file)

        return fetch(`/api_controller/upload_file/${customerDocumentId}`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          },
          body: formData,
        }).then(response => {
          if (!response.ok) {
            throw new Error(`Failed to upload file ${index + 1}`)
          }
          return response.json()
        })
      })

      // Wait for all uploads to complete
      await Promise.all(uploadPromises)

      // Now call prepopulate with the method flags
      const prepopulateFormData = new FormData()
      if (hasCameraImages) {
        prepopulateFormData.append('camera', 'true')
      }
      if (hasUploadImages) {
        prepopulateFormData.append('upload', 'true')
      }

      const prepopulateResponse = await fetch(
        `/api_controller/prepopulate_from_document_files/${customerDocumentId}`,
        {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          },
          body: prepopulateFormData,
        }
      )

      if (!prepopulateResponse.ok) {
        throw new Error('Failed to prepopulate from files')
      }

      const data = await prepopulateResponse.json()
      window.location.href = data.resume_url
    } catch (error) {
      console.error('Error processing images:', error)
      setIsProcessing(false)
      const customerDocumentId = getCustomerDocumentId()
      const manualEntryUrl = `/solosettles/${customerDocumentId}/steps/document_summary`
      setError(
        `An error occurred while processing your documents. You can <a href="${manualEntryUrl}">enter data manually</a> instead.`
      )
    }
  }

  // Helper function to convert data URI to Blob
  const dataURItoBlob = dataURI => {
    const byteString = atob(dataURI.split(',')[1])
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]
    const ab = new ArrayBuffer(byteString.length)
    const ia = new Uint8Array(ab)

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i)
    }

    return new Blob([ab], { type: mimeString })
  }

  if (!isMobile) {
    return (
      <div className='col-md-7'>
        <h1>Scan or Upload Document</h1>

        {error && (
          <Alert message='error' type='danger' allowHtml={true}>
            <span dangerouslySetInnerHTML={{ __html: error }} />
          </Alert>
        )}

        <p>
          Please provide the documents you recently received about your debt, one page at a time.
          Then click "Done". You can also enter data{' '}
          <a href={`/solosettles/${getCustomerDocumentId()}/steps/document_summary`}>manually</a>.
        </p>

        <input
          type='file'
          accept='image/*,application/pdf,.pdf'
          multiple
          onChange={handleFileUpload}
          disabled={isProcessing}
          style={{
            maxWidth: '300px',
          }}
        />

        {capturedImages.length > 0 && (
          <div
            style={{
              display: 'flex',
              gap: '10px',
              flexWrap: 'wrap',
              justifyContent: 'center',
              maxWidth: '500px',
            }}
          >
            {capturedImages.map((item, index) => {
              const dataUri = item.dataUri
              const type = item.type
              const isPdf = type.includes('pdf')

              return (
                <div
                  key={index}
                  style={{
                    width: '100px',
                    height: '140px',
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                  }}
                >
                  {isPdf ? (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '10px',
                      }}
                    >
                      <div
                        style={{
                          width: '60px',
                          height: '80px',
                          border: '2px solid #e0e0e0',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          color: '#e74c3c',
                        }}
                      >
                        PDF
                      </div>
                      <div
                        style={{
                          fontSize: '10px',
                          marginTop: '5px',
                          textAlign: 'center',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          width: '90px',
                        }}
                      >
                        File {index + 1}
                      </div>
                    </div>
                  ) : (
                    <img
                      src={dataUri}
                      alt={`Uploaded file ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  )}
                  <button
                    onClick={() => removeImage(index)}
                    disabled={isProcessing}
                    style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px',
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 0, 0, 0.7)',
                      color: 'white',
                      border: 'none',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      fontSize: '26px',
                      fontWeight: 'bold',
                      cursor: isProcessing ? 'not-allowed' : 'pointer',
                      opacity: isProcessing ? 0.5 : 1,
                      padding: 0,
                    }}
                  >
                    ×
                  </button>
                </div>
              )
            })}
          </div>
        )}

        <br />
        <br />

        <div>
          <button
            onClick={handleDone}
            disabled={capturedImages.length === 0 || isProcessing}
            className='btn btn-primary'
          >
            {isProcessing ? (
              <>
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #fff',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginRight: '8px',
                    display: 'inline-block',
                    verticalAlign: 'middle',
                  }}
                />
                <span style={{ verticalAlign: 'middle' }}>Processing...</span>
              </>
            ) : (
              `Done (${capturedImages.length})`
            )}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#000',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 9999,
      }}
    >
      {/* Instructions Header */}
      <div
        style={{
          padding: '16px',
          backgroundColor: '#fff',
          textAlign: 'center',
          fontSize: '16px',
          fontWeight: '500',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          zIndex: 2,
        }}
      >
        Please provide the documents you recently received about your debt, then click "Done". You
        can also enter data{' '}
        <a href={`/solosettles/${getCustomerDocumentId()}/steps/document_summary`}>manually</a>.
      </div>

      {/* Camera View / Upload Area */}
      <div
        style={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#1a1a1a',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '20px',
        }}
      >
        {/* Mode Toggle Buttons */}
        <div
          style={{
            marginBottom: '20px',
            display: 'flex',
            gap: '10px',
            zIndex: 3,
          }}
        >
          <button
            className={`btn ${showCamera ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setShowCamera(true)}
          >
            Use Camera
          </button>
          <button
            className={`btn ${!showCamera ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setShowCamera(false)}
          >
            Upload Files
          </button>
        </div>

        {/* Camera or Upload UI */}
        <div
          style={{
            flex: 1,
            width: '100%',
            position: 'relative',
          }}
        >
          {showCamera ? (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <div
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <input
                type='file'
                accept='image/*,application/pdf,.pdf'
                multiple
                onChange={handleFileUpload}
                disabled={isProcessing}
                style={{ display: 'none' }}
                id='fileInput'
              />
              <label
                htmlFor='fileInput'
                className='btn btn-secondary'
                style={{
                  width: '200px',
                  textAlign: 'center',
                }}
              >
                Choose Files
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Thumbnails - Fixed dimensions with scrolling */}
      <div
        style={{
          'position': 'absolute',
          'top': '80px',
          'right': '16px',
          'maxHeight': '60vh',
          'overflowY': 'auto',
          'display': 'flex',
          'flexDirection': 'column',
          'gap': '8px',
          'zIndex': 9999,
          'padding': '4px',
          'scrollbarWidth': 'thin',
          'scrollbarColor': '#2196f3 transparent',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#2196f3',
            borderRadius: '3px',
          },
        }}
      >
        {capturedImages.map((item, index) => {
          const dataUri = item.dataUri
          const type = item.type
          const isPdf = type.includes('pdf')

          return (
            <div
              key={index}
              style={{
                width: '60px',
                height: '80px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                flexShrink: 0, // Prevent thumbnail from shrinking
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
              }}
            >
              {isPdf ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: '#e74c3c',
                    width: '100%',
                    height: '100%',
                    border: '1px solid #e0e0e0',
                  }}
                >
                  PDF
                </div>
              ) : (
                <img
                  src={dataUri}
                  alt={`Captured file ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              )}
              <button
                onClick={() => removeImage(index)}
                disabled={isProcessing}
                style={{
                  position: 'absolute',
                  top: '2px',
                  right: '2px',
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 0, 0, 0.7)',
                  color: 'white',
                  border: 'none',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '26px',
                  fontWeight: 'bold',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  opacity: isProcessing ? 0.5 : 1,
                  padding: 0,
                }}
              >
                ×
              </button>
            </div>
          )
        })}
      </div>

      {/* Controls */}
      <div
        style={{
          padding: '20px',
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: capturedImages.length > 0 ? 'flex-start' : 'center',
          alignItems: 'center',
          gap: '20px',
        }}
      >
        {/* Capture Button - Only show in camera mode */}
        {showCamera && (
          <button
            onClick={captureImage}
            disabled={isProcessing}
            style={{
              width: '72px',
              height: '72px',
              borderRadius: '50%',
              backgroundColor: '#fff',
              border: '4px solid #2196f3',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              opacity: isProcessing ? 0.6 : 1,
            }}
          />
        )}

        {/* Done Button */}
        {capturedImages.length > 0 && (
          <button onClick={handleDone} disabled={isProcessing} className='btn btn-primary'>
            {isProcessing ? <>Processing...</> : `Done (${capturedImages.length})`}
          </button>
        )}
      </div>

      {/* Add keyframe animation for spinner */}
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  )
}

export default ScanDocumentRoot
